import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/Gallery/Gallery.css';

function Gallery() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sectionPages, setSectionPages] = useState({});
  const [animatingSections, setAnimatingSections] = useState({});
  
  // Lightbox state
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeSectionIdx, setActiveSectionIdx] = useState(null);
  const [activeItemIdx, setActiveItemIdx] = useState(null);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const res = await fetch('/api/gallery');
      const data = await res.json();
      setSections(data);
    } catch (err) {
      console.error('Error fetching gallery:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (sIdx, totalPages) => {
    if (animatingSections[sIdx]) return; // Prevent double clicks during transition

    const currentPage = sectionPages[sIdx] || 0;
    const nextPage = (currentPage + 1) % totalPages;

    // Trigger leaving zoom out animation
    setAnimatingSections(prev => ({ ...prev, [sIdx]: 'leaving' }));

    setTimeout(() => {
      // Change page and trigger entering zoom-in animation
      setSectionPages(prev => ({ ...prev, [sIdx]: nextPage }));
      setAnimatingSections(prev => ({ ...prev, [sIdx]: 'entering' }));

      // Scroll smoothly to this section's heading
      const sectionElement = document.getElementById(`gallery-sec-${sIdx}`);
      if (sectionElement) {
        const yOffset = -120; // Clears the fixed navigation header perfectly
        const y = sectionElement.getBoundingClientRect().top + window.scrollY + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }

      setTimeout(() => {
        // Clear animation class when done
        setAnimatingSections(prev => ({ ...prev, [sIdx]: null }));
      }, 300);
    }, 300);
  };

  const openLightbox = (sectionIdx, itemIdx) => {
    const item = sections[sectionIdx].items[itemIdx];
    if (item.resource_type === 'image') {
      setSelectedImage(item.url);
      setActiveSectionIdx(sectionIdx);
      setActiveItemIdx(itemIdx);
      document.body.style.overflow = 'hidden'; // Prevent scroll
    }
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    setActiveSectionIdx(null);
    setActiveItemIdx(null);
    document.body.style.overflow = 'auto';
  };

  const navigateImage = (direction) => {
    const currentSection = sections[activeSectionIdx];
    const imageOnlyIndices = currentSection.items
      .map((item, idx) => item.resource_type === 'image' ? idx : null)
      .filter(idx => idx !== null);

    const currentPos = imageOnlyIndices.indexOf(activeItemIdx);
    let nextPos = currentPos + direction;

    if (nextPos >= imageOnlyIndices.length) nextPos = 0;
    if (nextPos < 0) nextPos = imageOnlyIndices.length - 1;

    const nextItemIdx = imageOnlyIndices[nextPos];
    setActiveItemIdx(nextItemIdx);
    setSelectedImage(currentSection.items[nextItemIdx].url);
  };

  return (
    <div className="gallery-page">
      <Header />
      <section className="gallery-section">
        <div className="gallery-container">
          
          {loading ? (
            <div className="gallery-loading">
                <div className="spinner"></div>
                <p>Loading our hospital gallery...</p>
            </div>
          ) : sections.length === 0 ? (
            <div className="gallery-empty">
                <p>New photos coming soon!</p>
            </div>
          ) : (
            sections.map((section, sIdx) => {
              const currentPage = sectionPages[sIdx] || 0;
              const totalPages = Math.ceil(section.items.length / 6);
              const displayedItems = section.items.slice(currentPage * 6, (currentPage + 1) * 6);
              const animState = animatingSections[sIdx];
              let animClass = '';
              if (animState === 'leaving') animClass = 'anim-zoom-leave';
              else if (animState === 'entering') animClass = 'anim-zoom-enter';

              return (
                <div key={section._id} id={`gallery-sec-${sIdx}`} className="gallery-section-group">
                  <h2 className="gallery-heading">{section.title}</h2>
                  <div className={`gallery-grid ${animClass}`}>
                    {displayedItems.map((item, iIdx) => {
                      const globalIdx = currentPage * 6 + iIdx;
                      return (
                        <div key={item._id} className="gallery-item">
                          <div className="gallery-img-wrapper" onClick={() => openLightbox(sIdx, globalIdx)}>
                            {item.resource_type === 'video' ? (
                              <video 
                                src={item.url} 
                                controls 
                                playsInline
                                className="gallery-video"
                              />
                            ) : (
                              <img src={item.url} alt={section.title} className="gallery-clickable-img" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {totalPages > 1 && (
                    <div className="gallery-view-more-container">
                      <button 
                        className="gallery-view-more-btn"
                        onClick={() => handlePageChange(sIdx, totalPages)}
                      >
                        {currentPage === totalPages - 1 ? (
                          <>Show Less <span className="arrow">←</span></>
                        ) : (
                          <>View More <span className="arrow">→</span></>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}

        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <button className="lightbox-close" onClick={closeLightbox}>✕</button>
          
          <button 
            className="lightbox-arrow lb-left" 
            onClick={(e) => { e.stopPropagation(); navigateImage(-1); }}
          >
            ‹
          </button>
          
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img src={selectedImage} alt="Fullscreen View" />
          </div>

          <button 
            className="lightbox-arrow lb-right" 
            onClick={(e) => { e.stopPropagation(); navigateImage(1); }}
          >
            ›
          </button>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default Gallery;
